import { ScoreService, ScoreWithVariation } from "src/services/ScoreService";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { EntityScoresInit, EntityScoresContext, EntityScoresUnavailable } from "./context";
import { getDefaultVariationIdsByType, getVariationIdsByType, getVariationOrderByType } from "src/utils/scores";
import { useVariations } from "src/context/OlapScores/useVariations";
import { Score } from "src/types";
import { useEffect, useState } from "react";
import { NoScoresAvailableMessage } from "../../components/Message/NoScoresAvailableMessage";
import { NoScoresForSsqMessage } from "../../components/Message/NoScoresForSsqMessage";
import { Loading } from "src/components/utils/Loading";
import { useOverridenVariationsIds } from "./hooks/useOverridenVariationsIds";
import { useOverridenDefaultVariationsIds } from "./hooks/useOverridenDefaultVariationsIds";
import { formatImpact, getImpactColor, getScoreImpactFormatOptions } from "./utils/impactDisplay";
import { useTheme } from "@mui/material";
import { useLanguage } from "../LanguageProvider";
import { SsqVersionSsq } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";
import { useGetAllScore } from "src/api/sure-api/score/useGetAllScore";
import { getRemoteErrorStatus } from "src/api/remoteCollectionState";
import { ScoreQueryError } from "src/components/ScoreState/ScoreQueryError";
import { scoreHasBaseline } from "src/utils/actionPlanScoreFilters";

export const DEFAULT_SCORE_PRECISION: number = 3;

export const getScorePrecision = (score: { precision?: number }): number => score.precision ?? DEFAULT_SCORE_PRECISION;

export type EntityScoresProviderProps = {
    entity: EntityScoresInit["entity"];
    ssq?: SsqVersionSsq | undefined;
    allowMissingScores?: boolean;
    children: ReactNode;
};
export const EntityScoresProvider = (props: EntityScoresProviderProps) => {
    const { entity, ssq, allowMissingScores = false, children } = props;
    const {
        getScoresByType,
        retryScoreTypes,
        scoreTypes,
        scoreTypesError,
        scoreTypesLoading,
        scoreTypesStatus,
        scoresLoading,
    } = useFoodpilotContext();
    const { scores, error: scoresError, isError: isScoresError, refetch: retryScores } = useGetAllScore();
    const { currentLanguage } = useLanguage();

    const theme = useTheme();

    const [entitySsq, setEntitySsq] = useState<SsqVersionSsq | undefined>(ssq);

    useEffect(() => {
        setEntitySsq(ssq);
    }, [ssq]);

    const entityScores = getScoresByType(entity) || [];
    const entityVariationsIds = getVariationIdsByType(entity, scoreTypes, entityScores);
    const defaultVariationsIds = getDefaultVariationIdsByType(entity, scoreTypes);

    const overridenVariationsIds = useOverridenVariationsIds(entity);
    const overridenDefaultVariationsIds = useOverridenDefaultVariationsIds(entity);

    const entityOverridenDefaultVariationsIds = overridenVariationsIds ? overridenDefaultVariationsIds : undefined;
    const entityDefaultVariationsIds = entityOverridenDefaultVariationsIds ?? defaultVariationsIds ?? [];
    const initVariations = ScoreService.getInitialVariations(entityScores, entityDefaultVariationsIds);
    const selectedVariations = useVariations(initVariations);

    if (scoreTypesStatus === "error") {
        return (
            <ScoreQueryError
                context="catalog"
                onRetry={retryScoreTypes}
                status={getRemoteErrorStatus(scoreTypesError)}
            />
        );
    }

    if (isScoresError) {
        return <ScoreQueryError context="catalog" onRetry={retryScores} status={getRemoteErrorStatus(scoresError)} />;
    }

    if (!scores || !scoreTypes || scoreTypesLoading || scoreTypesStatus !== "success" || scoresLoading) {
        return <Loading />;
    }

    const unavailableScores: EntityScoresUnavailable = { isAvailable: false, entity, entitySsq, setEntitySsq };
    const renderWithoutScores = () => (
        <EntityScoresContext.Provider value={unavailableScores}>{children}</EntityScoresContext.Provider>
    );

    if (entitySsq && entitySsq.scores.length === 0) {
        return allowMissingScores ? renderWithoutScores() : <NoScoresForSsqMessage />;
    }

    const getEntityScore = (scoreId: number): Score | undefined => {
        return entityScores.find((score: Score) => score.id === scoreId);
    };

    const scoresWithVariations = ScoreService.getScoresWithVariations(
        entityScores,
        overridenVariationsIds ?? entityVariationsIds,
        entityDefaultVariationsIds,
        getVariationOrderByType(entity, scoreTypes),
        (scoreId, scoreVariationId) => {
            selectedVariations.state.selectVariation(scoreId, scoreVariationId);
        },
    );

    // Filter scoreList to only keep scores belonging to this questionnaire - NO useMemo to avoid hook order issues
    const ssqScoresWithVariations =
        !entitySsq ? scoresWithVariations : (
            (() => {
                const ssqScoreIds = ScoreService.getScoreIdsFromSsq(entitySsq, []);

                const filtered = scoresWithVariations.filter((scoreWithVariation) =>
                    ssqScoreIds.includes(scoreWithVariation.scoreId),
                );

                return filtered;
            })()
        );

    const diagnosticScoresWithVariations = ssqScoresWithVariations.filter((scoreWithVariation) => {
        const score = getEntityScore(scoreWithVariation.scoreId);
        return score === undefined || scoreHasBaseline(score);
    });

    const initialSsqScoreWithVariations =
        ssqScoresWithVariations.find((scoreWithVariation: ScoreWithVariation) => {
            return (
                selectedVariations.state.variations?.[scoreWithVariation.scoreId] &&
                selectedVariations.state.variations?.[scoreWithVariation.scoreId] === scoreWithVariation.variationId
            );
        }) ??
        diagnosticScoresWithVariations[0] ??
        ssqScoresWithVariations[0] ??
        null;

    const lastSelectedVariation =
        ssqScoresWithVariations.find((scoreWithVariation: ScoreWithVariation) => {
            return (
                scoreWithVariation.scoreId === selectedVariations.state.lastSelectedVariation?.scoreId &&
                scoreWithVariation.variationId === selectedVariations.state.lastSelectedVariation?.variationId
            );
        }) ?? initialSsqScoreWithVariations;

    if (lastSelectedVariation === null) {
        return allowMissingScores ? renderWithoutScores() : <NoScoresAvailableMessage />;
    }

    const selectedScorePrecision = lastSelectedVariation.precision ?? DEFAULT_SCORE_PRECISION;

    const getColor: EntityScoresInit["getColor"] = (score, options = {}) => {
        if (score === null || score === undefined) {
            return undefined;
        }
        const { improvement = lastSelectedVariation.improvement, precision = selectedScorePrecision } = options;

        return getImpactColor(score, precision, improvement, theme);
    };
    const formatScore: EntityScoresInit["formatScore"] = (score, options = {}) => {
        if (score === null || score === undefined) {
            return null;
        }
        const { precision = selectedScorePrecision } = options;

        return formatImpact(score, precision, currentLanguage, getScoreImpactFormatOptions(precision));
    };

    const items: EntityScoresInit = {
        isAvailable: true,
        entity,
        entityScores,
        getEntityScore,
        getColor,
        formatScore,
        scoresWithVariations: ssqScoresWithVariations,
        allScoresWithVariations: scoresWithVariations,
        diagnosticScoresWithVariations,
        selectedVariations,
        selectVariation: selectedVariations.state.selectVariation,

        lastSelectedVariation,
        selectedScorePrecision,
        entitySsq,
        setEntitySsq,
    };

    return <EntityScoresContext.Provider value={items}>{children}</EntityScoresContext.Provider>;
};
